import type { CWSSummary } from '@dispatch/shared/types';
import { useEffect, useRef } from 'react';

/**
 * The Date of Loss is locked only when the claim lookup supplied it. A lookup
 * without a loss address populates nothing, so it does not lock either. This
 * keys off the lookup result, never the field value, so typing a date by hand
 * cannot lock the field mid-entry.
 */
export const isDateOfLossLocked = (summary: CWSSummary | undefined): boolean =>
  Boolean(summary?.lossDate && summary?.lossAddress);

/**
 * The FNOL Request Date of Loss as an ISO string, or '' when there is none.
 * The picker writes an ISO string while a looked-up value is a Date.
 */
export const toDateOfLossIso = (value: unknown): string => {
  if (!(value instanceof Date) && typeof value !== 'string') return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toISOString();
};

/**
 * Calls `clear` when the Date of Loss goes from locked to editable, so a
 * looked-up date does not linger for a claim the lookup cannot resolve. A date
 * typed while editable is kept across further lookups that stay editable.
 */
export const useClearDateOfLossOnUnlock = (
  isDolLocked: boolean,
  clear: () => void
): void => {
  const wasLocked = useRef(isDolLocked);

  useEffect(() => {
    if (wasLocked.current && !isDolLocked) {
      clear();
    }
    wasLocked.current = isDolLocked;
    // Runs on lock transitions only; `clear` is rebuilt every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDolLocked]);
};
